<template>
  <div
    v-auto-animate
    class="rounded-md w-full"
  >
    <!-- Main Form Row -->
    <div :class="$attrs.classList || 'flex flex-col sm:flex-row gap-4'">
      <!-- Country Selector -->
      <!-- <div
        v-if="!hideCountryField"
        class="country-field w-full"
      >
        <label
          for="country"
          class="block text-gray-500 text-sm mb-1"
        >Country</label>
        <Dropdown
          id="country"
          v-model="selectedCountry"
          :options="countries"
          optionLabel="name"
          optionValue="code"
          placeholder="Select a country"
          class="w-full"
        />
      </div> -->

      <!-- Address Input -->
      <div
        class="address-field w-full flex flex-col space-y-1"
        :class="{ 'w-full': !hideCountryField }"
      >
        <label
          v-if="showAddressLabel"
          for="address"
          class="text-sm"
        >{{ label }}</label>
        <div class="flex items-center">
          <InputText
            id="address"
            ref="autocompleteInput"
            v-model="autocompleteValue"
            class="w-full"
            placeholder="Enter a location"
            :disabled="disabledInputField"
          />
          <!-- <button
            class="ml-2 p-2 text-gray-600 hover:text-gray-800 focus:outline-none"
            aria-label="Toggle address details"
            @click.prevent="toggleShowInputs"
          >
            <Icon
              :name="showIndividualFields ? 'lucide:chevron-up' : 'lucide:chevron-down'
              "
            />
          </button> -->
        </div>
      </div>
    </div>

    <!-- Additional Address Details -->
    <div
      v-if="showIndividualFields"
      class="mt-4 bg-gray-100 p-4 rounded-md"
    >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div
          v-for="(label, key) in fieldLabels"
          :key="key"
        >
          <label
            :for="key"
            class="block text-gray-500 text-sm mb-1"
          >{{
            label
          }}</label>
          <InputText
            :id="key"
            v-model="address[key]"
            class="w-full"
            disabled
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

interface Address {
  fullAddress: string
  state: string
  country: string
  lat: number
  lng: number
}

const nuxtApp = useNuxtApp()

// Flags and reactive properties
const showIndividualFields = ref(false)
const autocompleteValue = ref('')
const autocompleteInput = ref<any>(null)
const autocompleteInstance = ref<any>(null)

const showAddressLabel = defineModel<boolean>('showAddressLabel', {
  type: Boolean,
  required: false,
  default: true,
})

// The address model may come pre-populated.
const address = defineModel<Address>('address', {
  required: true,
  default: {
    fullAddress: '',
    state: '',
    country: '',
    lat: 0,
    lng: 0,
  },
})

const disabledInputField = defineModel<boolean>('disabledInputField', {
  type: Boolean,
  required: true,
  default: true,
})
const hideCountryField = defineModel<boolean>('hideCountryField', {
  type: Boolean,
  required: false,
  default: false,
})

// ISO codes for countries (United Kingdom is now "GB")
const countries = [
  { name: 'Australia', code: 'AU', label: 'AUSTRALIA' },
  { name: 'United States', code: 'US', label: 'UNITED_STATES' },
  { name: 'United Kingdom', code: 'GB', label: 'UNITED_KINGDOM' },
  { name: 'Canada', code: 'CA', label: 'CANADA' },
]
const selectedCountry = ref('AU')

const fieldLabels = {
  state: 'State',
  country: 'Country',
  lat: 'Latitude',
  lng: 'Longitude',
}

// A flag to prevent the watcher from acting during initial load.
let initialLoad = true
const label = defineModel<string>('label', {
  type: String,
  required: false,
  default: 'Address',
})

const { getWorkSpace: getWorkSpaceFromDB } = useWorkSpace()
const getCountryCode = async () => {
  const res = await getWorkSpaceFromDB()
  const foundCountry = countries.find(el => el.label == res?.data?.country)
  if (foundCountry) {
    console.log('Found country:', foundCountry)
    selectedCountry.value = foundCountry?.code
  }
}

onMounted(async () => {
  await getCountryCode()

  const googleMapsLoader = nuxtApp.$googleMapsLoader
  await googleMapsLoader.load()
  console.log('Google Maps API loaded')

  // If an address exists and has a country, update selectedCountry.
  // if (address.value?.country) {
  //   const found = countries.find(
  //     c => c.name.toLowerCase() === address.value.country.toLowerCase(),
  //   )
  //   if (found) {
  //     selectedCountry.value = found.code
  //   }
  // }

  // Populate the autocomplete input with the prepopulated address.
  autocompleteValue.value = address.value?.fullAddress

  // Initialize the autocomplete now that the API is ready.
  initializeAutocomplete(address.value?.fullAddress)

  // Initial load is complete.
  initialLoad = false
})

// Function to (re)initialize the autocomplete instance.
const initializeAutocomplete = (fulladress) => {
  if (!window.google || !autocompleteInput.value) return

  // (Re)create the autocomplete instance with current country restrictions.
  autocompleteInstance.value = new window.google.maps.places.Autocomplete(
    autocompleteInput.value.$el,
    {
      fields: ['address_components', 'geometry'],
      types: ['address'],
    },
  )
  if (fulladress) {
    autocompleteInput.value.$el.value = fulladress
  }

  autocompleteInstance.value.addListener('place_changed', () => {
    const formattedPrediction = autocompleteInput.value.$el.value
    console.log('Formatted prediction:', formattedPrediction)
    fillInAddress(formattedPrediction, autocompleteInstance.value)
  })
}

watch(address, (newAddress) => {
  if (newAddress) {
    // Populate the autocomplete input with the prepopulated address.
    autocompleteValue.value = newAddress.fullAddress

    // Initialize the autocomplete now that the API is ready.
    initializeAutocomplete(newAddress.fullAddress)
  }
})

// Watch for changes in selectedCountry after initial load.
watch(selectedCountry, (newCountry, oldCountry) => {
  console.log(`Country changed from ${oldCountry} to ${newCountry}`)
  //    check if the current address is the same country as the selected country
  if (
    address.value.country == countries.find(c => c?.code === newCountry)?.name
  ) {
    console.log('Country mismatch')
    initializeAutocomplete(address.value.fullAddress)
  }
  else {
    autocompleteValue.value = ''
    clearAddress()
    autocompleteInput.value.$el.value = ''
    initializeAutocomplete('')
  }
})

const toggleShowInputs = () => {
  showIndividualFields.value = !showIndividualFields.value
}

const fillInAddress = (formattedPrediction: string, autocomplete: any) => {
  const place = autocomplete.getPlace()
  // if (!place.geometry) {
  //   nuxtApp.$toast.error("Please select a valid address");
  //   return;
  // }

  const latitude = place.geometry.location.lat()
  const longitude = place.geometry.location.lng()
  let tempState = ''
  let tempCountry = ''

  // Extract desired components from the place result.
  place.address_components.forEach((component: any) => {
    const { types, long_name } = component
    if (types.includes('administrative_area_level_1')) {
      tempState = long_name
    }
    else if (types.includes('country')) {
      tempCountry = long_name
    }
  })

  // Update the address model.
  address.value = {
    fullAddress: formattedPrediction,
    state: tempState,
    country: tempCountry,
    lat: latitude,
    lng: longitude,
  }

  // if (!hasAllFields()) {
  //   nuxtApp.$toast.error("Please select a valid address");
  // }
}

const clearAddress = () => {
  address.value = {
    fullAddress: '',
    state: '',
    country: '',
    lat: 0,
    lng: 0,
  }
}

const hasAllFields = () => {
  const { fullAddress, lat, lng } = address.value
  return !!(fullAddress && lat && lng)
}
</script>

<style>
.autocomplete-input {
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
}

.pac-container,
.pac-item {
  z-index: 2147483647 !important;
}

.fullwidth .country-field,
.fullwidth .address-field {
  width: 100%;
}
</style>
